// frontend/src/components/billing/address-autocomplete.tsx
// Read DESIGN.md before modifying this file.
// A single billing-address search box (Google Places "new" autocomplete). The
// customer types one line and picks a result; we capture the structured address
// (country/line1/city/state/postal). Once an address is chosen, the parsed
// fields appear below, pre-filled and editable, so the customer can correct
// anything. A billing address must resolve to a specific building, so a pick
// without a street number is flagged. Apt/suite/line2 is intentionally not
// collected for billing. When VITE_GOOGLE_PLACES_API_KEY is absent (or Maps JS
// fails to load), the component falls back to plain manual address fields.
import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { countryName, COUNTRIES } from '@/lib/countries';
import {
  loadPlaceAutocompleteElement,
  parseAddressComponents,
  hasStreetNumber,
  type GmpSelectEvent,
} from '@/lib/google-places';

export interface AddressValue {
  country: string;
  line1: string;
  city: string;
  state: string;
  postalCode: string;
}

function summarize(v: AddressValue): string {
  return [v.line1, v.city, [v.state, v.postalCode].filter(Boolean).join(' '), v.country]
    .filter(Boolean)
    .join(', ');
}

const LOCKED_REASON =
  'Set from your address search. Pick a new address above to change it.';

// A read-only address field with a tooltip explaining why it can't be edited
// directly. The disabled input swallows pointer events, so the tooltip triggers
// on the wrapping span instead.
function LockedField({
  id,
  label,
  value,
}: {
  id: string;
  label: string;
  value: string;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Tooltip>
        <TooltipTrigger render={<span className="block" />}>
          <Input id={id} value={value} disabled readOnly />
        </TooltipTrigger>
        <TooltipContent>{LOCKED_REASON}</TooltipContent>
      </Tooltip>
    </div>
  );
}

export function AddressAutocomplete({
  value,
  onChange,
}: {
  value: AddressValue;
  onChange: (v: AddressValue) => void;
}) {
  const [loading, setLoading] = useState(true);
  // Manual entry: no Places key configured, or Maps JS failed to load. Plain
  // editable fields beat a dead search box in both cases.
  const [manual, setManual] = useState(false);
  const [missingNumber, setMissingNumber] = useState(false);
  // True when the box holds typed text that was never resolved to a picked
  // address (e.g. "tel aviv" then Tab away). Drives the "pick a suggestion" error.
  const [notSelected, setNotSelected] = useState(false);
  const unresolvedRef = useRef(false);
  const hostRef = useRef<HTMLDivElement | null>(null);
  const widgetRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    let widget: HTMLElement | null = null;
    let cancelled = false;

    const onSelect = async (event: Event) => {
      // A real pick resolves the text — clear the unresolved flag synchronously,
      // before the await, so the blur check (deferred) sees it cleared.
      unresolvedRef.current = false;
      setNotSelected(false);
      const place = (event as GmpSelectEvent).placePrediction.toPlace();
      await place.fetchFields({ fields: ['addressComponents', 'formattedAddress'] });
      if (cancelled) return;
      const components = place.addressComponents ?? [];
      setMissingNumber(!hasStreetNumber(components));
      onChange(parseAddressComponents(components));
    };

    // composedPath()[0] is the real <input> inside the widget's shadow DOM
    // (event.target is retargeted to the host). Non-empty text that hasn't been
    // picked is "unresolved"; clearing the box clears it.
    const onInput = (event: Event) => {
      const input = event.composedPath()[0] as HTMLInputElement | undefined;
      unresolvedRef.current = (input?.value ?? '').trim().length > 0;
      setNotSelected(false);
    };

    // focusout fires before gmp-select when clicking a suggestion, so defer the
    // check a tick to let a pending selection clear the flag first.
    const onBlur = () => {
      setTimeout(() => {
        if (!cancelled && unresolvedRef.current) setNotSelected(true);
      }, 0);
    };

    (async () => {
      try {
        const PlaceAutocompleteElement = await loadPlaceAutocompleteElement();
        if (cancelled) return;
        if (!PlaceAutocompleteElement) {
          setManual(true);
          setLoading(false);
          return;
        }
        widget = new PlaceAutocompleteElement();
        widget.classList.add('w-full');
        // Replace Google's gray magnifier with our lucide search icon (muted,
        // size-4) via the widget's `input-icon` slot, matching the app search.
        const icon = document.createElement('span');
        icon.setAttribute('slot', 'input-icon');
        icon.innerHTML =
          '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.3-4.3"></path></svg>';
        widget.appendChild(icon);
        // Suppress Google's near-black clear "×" — slot it with empty content
        // (CSS hides it) so the field reads like our plain search inputs.
        const clearIcon = document.createElement('span');
        clearIcon.setAttribute('slot', 'clear-icon');
        widget.appendChild(clearIcon);
        widget.addEventListener('gmp-select', onSelect);
        widget.addEventListener('input', onInput);
        widget.addEventListener('focusout', onBlur);
        widgetRef.current = widget;
        hostRef.current?.replaceChildren(widget);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        console.error('[address-autocomplete] mount failed', e);
        setManual(true);
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      widget?.removeEventListener('gmp-select', onSelect);
      widget?.removeEventListener('input', onInput);
      widget?.removeEventListener('focusout', onBlur);
      widgetRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show the saved address inside the box (as the placeholder), so on load the
  // field reads like the chosen address instead of sitting empty.
  useEffect(() => {
    const widget = widgetRef.current as (HTMLElement & { placeholder?: string }) | null;
    if (!widget) return;
    widget.placeholder = value.line1 ? summarize(value) : 'Search for your address';
  }, [value, loading]);

  if (manual) {
    const set = (patch: Partial<AddressValue>) => onChange({ ...value, ...patch });
    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="billing-line1">Billing address</Label>
          <Input
            id="billing-line1"
            value={value.line1}
            placeholder="Street and number"
            onChange={(e) => set({ line1: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="billing-city">City</Label>
            <Input
              id="billing-city"
              value={value.city}
              onChange={(e) => set({ city: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="billing-state">State / Region</Label>
            <Input
              id="billing-state"
              value={value.state}
              onChange={(e) => set({ state: e.target.value })}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="billing-postal">Postal code</Label>
            <Input
              id="billing-postal"
              value={value.postalCode}
              onChange={(e) => set({ postalCode: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="billing-country">Country</Label>
            <Select
              value={value.country || undefined}
              onValueChange={(c) => c && set({ country: c })}
            >
              <SelectTrigger id="billing-country" aria-label="Country" className="w-full">
                <SelectValue>{(c) => countryName((c as string) ?? '') || 'Select country'}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {COUNTRIES.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label htmlFor="billing-address-search">Billing address</Label>
      <div ref={hostRef} id="billing-address-search" />
      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Loading address search...
        </div>
      )}
      {missingNumber && (
        <p role="alert" className="text-sm text-destructive">
          Add the building or house number to your address.
        </p>
      )}
      {notSelected && (
        <p role="alert" className="text-sm text-destructive">
          Select your address from the suggestions.
        </p>
      )}

      {/* After a pick (or a previously-saved address), show the parsed fields
          read-only — the breakdown is for review only. Editing happens ONLY via
          the search box above, which is where the building-number rule is
          enforced. Postal code is captured silently and saved to Stripe (Google
          omits it for street-level picks), so it isn't shown. */}
      {value.line1 && (
        <div className="space-y-4 pt-2">
          <LockedField id="billing-line1" label="Address" value={value.line1} />
          <div className="grid grid-cols-2 gap-3">
            <LockedField id="billing-city" label="City" value={value.city} />
            <LockedField
              id="billing-state"
              label="State / Region"
              value={value.state}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <LockedField
              id="billing-country"
              label="Country"
              value={countryName(value.country)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
